'use client'

import { useEffect, useState, useCallback, useRef } from 'react'

/**
 * Chat widget powered by Tawk.to
 * - Custom launcher bubble (Tawk's default widget is permanently hidden)
 * - Click bubble → shows name/email form → opens Tawk chat
 * - Removes branding inside Tawk iframe
 */

declare global {
    interface Window {
        Tawk_API?: any
        Tawk_LoadStart?: Date
    }
}

const TAWK_SRC = 'https://embed.tawk.to/6a169adc44ecd61c305bd6c6/1jpk4prn0'
const LC_STORAGE_KEY = 'lc-chat-user'

// CSS to permanently hide Tawk's default bubble
const TAWK_NUKE_CSS = `
.tawk-min-container,
.tawk-button-circle,
.tawk-button,
#tawk-default,
div[class*="tawk"][style*="z-index"]:not([class*="tawk-chat"]) {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    width: 0 !important;
    height: 0 !important;
    overflow: hidden !important;
    pointer-events: none !important;
    position: fixed !important;
    top: -9999px !important;
    left: -9999px !important;
}
`

export function CrispChat() {
    const [chatOpen, setChatOpen] = useState(false)
    const [showForm, setShowForm] = useState(false)
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const scriptLoaded = useRef(false)

    useEffect(() => {
        if (scriptLoaded.current) return
        scriptLoaded.current = true

        // 1. Nuke CSS — before anything else
        if (!document.getElementById('tawk-nuke')) {
            const s = document.createElement('style')
            s.id = 'tawk-nuke'
            s.textContent = TAWK_NUKE_CSS
            document.head.appendChild(s)
        }

        // 2. Configure Tawk API
        window.Tawk_API = window.Tawk_API || {}
        window.Tawk_LoadStart = new Date()

        window.Tawk_API.onLoad = () => {
            window.Tawk_API?.hideWidget?.()
        }

        window.Tawk_API.onChatMaximized = () => {
            setChatOpen(true)
        }

        window.Tawk_API.onChatMinimized = () => {
            setChatOpen(false)
            window.Tawk_API?.hideWidget?.()
        }

        window.Tawk_API.onChatHidden = () => {
            setChatOpen(false)
        }

        // 3. Load Tawk script
        const s1 = document.createElement('script')
        s1.async = true
        s1.src = TAWK_SRC
        s1.charset = 'UTF-8'
        s1.setAttribute('crossorigin', '*')
        document.head.appendChild(s1)

        // 4. Inject launcher CSS
        if (!document.getElementById('lc-style')) {
            const style = document.createElement('style')
            style.id = 'lc-style'
            style.textContent = LAUNCHER_CSS
            document.head.appendChild(style)
        }

        // 5. Branding removal
        const normalizeText = (t: string) => t.replace(/\s+/g, ' ').trim().toLowerCase()
        const removeBranding = () => {
            document.querySelectorAll('iframe').forEach((ifr) => {
                try {
                    const doc = ifr.contentDocument || ifr.contentWindow?.document
                    if (!doc) return
                    const btn = doc.querySelector('a[class*="tawk-button-small"]')
                    if (btn) btn.remove()
                    const popoutIcon = doc.querySelector('i.tawk-icon.tawk-icon-popout')
                    if (popoutIcon) popoutIcon.closest('button')?.remove()
                    const byHref = doc.querySelector('a[href*="tawk.to/?utm_source"], a[href*="tawk.to/?utm_"]')
                    if (byHref) { byHref.remove(); return }
                    const svgBrand = doc.querySelector('a svg[viewBox="0 0 107 15"]')
                    if (svgBrand) { svgBrand.closest('a')?.remove(); return }
                    const nodes = Array.from(doc.querySelectorAll('a, div, span'))
                    const pw = nodes.find(el => normalizeText(el.textContent || '').includes('powered by tawk.to'))
                    if (pw) { const a = pw.tagName === 'A' ? pw : pw.querySelector('a'); (a || pw).remove() }
                } catch (e) { /* cross-origin safe */ }
            })
        }
        const interval = setInterval(removeBranding, 300)
        return () => clearInterval(interval)
    }, [])

    const setTawkUser = useCallback((userName: string, userEmail: string) => {
        if (!window.Tawk_API) return
        if (userName) {
            window.Tawk_API.visitor = {
                name: userName,
                email: userEmail || undefined,
            }
            try {
                window.Tawk_API.setAttributes?.({
                    name: userName,
                    ...(userEmail ? { email: userEmail } : {}),
                }, (error: any) => { /* silent */ })
            } catch (_) { /* silent */ }
        }
    }, [])

    const openChat = useCallback(() => {
        if (!window.Tawk_API) return
        window.Tawk_API.showWidget?.()
        window.Tawk_API.maximize?.()
        setChatOpen(true)
        setShowForm(false)
    }, [])

    const handleLauncherClick = useCallback(() => {
        // If user already submitted their info before, skip the form
        try {
            const saved = localStorage.getItem(LC_STORAGE_KEY)
            if (saved) {
                const { name: savedName, email: savedEmail } = JSON.parse(saved)
                if (savedName) {
                    setTawkUser(savedName, savedEmail)
                    openChat()
                    return
                }
            }
        } catch (_) { /* localStorage unavailable */ }
        setShowForm(true)
    }, [setTawkUser, openChat])

    const handleFormSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault()
        const trimmedName = name.trim()
        const trimmedEmail = email.trim()
        // Save to localStorage so form won't show again
        try {
            localStorage.setItem(LC_STORAGE_KEY, JSON.stringify({ name: trimmedName, email: trimmedEmail }))
        } catch (_) { /* silent */ }
        // Pass to Tawk
        setTawkUser(trimmedName, trimmedEmail)
        openChat()
    }, [name, email, setTawkUser, openChat])

    const handleFormClose = useCallback(() => {
        setShowForm(false)
    }, [])

    return (
        <>
            {/* Name/Email form — appears above launcher */}
            {showForm && !chatOpen && (
                <div className="lc-form-overlay" onClick={handleFormClose}>
                    <div className="lc-form-card" onClick={(e) => e.stopPropagation()}>
                        <button className="lc-form-close" onClick={handleFormClose} aria-label="Close form">
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M11 3L3 11M3 3l8 8" stroke="#999" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </button>
                        <div className="lc-form-header">
                            <div className="lc-form-icon">
                                <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
                                    <path fill="#111" d="M12.63,26.46H8.83a6.61,6.61,0,0,1-6.65-6.07,89.05,89.05,0,0,1,0-11.2A6.5,6.5,0,0,1,8.23,3.25a121.62,121.62,0,0,1,15.51,0A6.51,6.51,0,0,1,29.8,9.19a77.53,77.53,0,0,1,0,11.2,6.61,6.61,0,0,1-6.66,6.07H19.48L12.63,31V26.46"/>
                                    <path fill="#dddd2d" d="M19.57,21.68h3.67a2.08,2.08,0,0,0,2.11-1.81,89.86,89.86,0,0,0,0-10.38,1.9,1.9,0,0,0-1.84-1.74,113.15,113.15,0,0,0-15,0A1.9,1.9,0,0,0,6.71,9.49a74.92,74.92,0,0,0-.06,10.38,2,2,0,0,0,2.1,1.81h3.81V26.5Z"/>
                                </svg>
                            </div>
                            <div className="lc-form-title">Start a conversation</div>
                            <div className="lc-form-subtitle">We typically reply within a few minutes</div>
                        </div>
                        <form className="lc-form" onSubmit={handleFormSubmit}>
                            <div className="lc-field">
                                <label className="lc-label" htmlFor="lc-name">Name</label>
                                <input
                                    id="lc-name"
                                    className="lc-input"
                                    type="text"
                                    placeholder="Your name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    autoFocus
                                    autoComplete="name"
                                />
                            </div>
                            <div className="lc-field">
                                <label className="lc-label" htmlFor="lc-email">Email</label>
                                <input
                                    id="lc-email"
                                    className="lc-input"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                />
                            </div>
                            <button className="lc-submit" type="submit">
                                Start chatting
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </form>
                        <button className="lc-skip" type="button" onClick={openChat}>
                            Skip, chat without details
                        </button>
                    </div>
                </div>
            )}

            {/* Chat launcher — visible when chat is closed */}
            {!chatOpen && !showForm && (
                <div className="lc-launcher" onClick={handleLauncherClick} role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLauncherClick() }}
                    aria-label="Open chat"
                >
                    <div className="lc-input-bar">
                        <span className="lc-input-text">Write a message...</span>
                        <div className="lc-input-send">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M8 13V3M8 3L3.5 7.5M8 3l4.5 4.5" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                    </div>
                    <div className="lc-bubble">
                        <svg className="lc-svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                            <path fill="#111111" d="M12.63,26.46H8.83a6.61,6.61,0,0,1-6.65-6.07,89.05,89.05,0,0,1,0-11.2A6.5,6.5,0,0,1,8.23,3.25a121.62,121.62,0,0,1,15.51,0A6.51,6.51,0,0,1,29.8,9.19a77.53,77.53,0,0,1,0,11.2,6.61,6.61,0,0,1-6.66,6.07H19.48L12.63,31V26.46"/>
                            <path className="lc-inner" fill="#dddd2d" d="M19.57,21.68h3.67a2.08,2.08,0,0,0,2.11-1.81,89.86,89.86,0,0,0,0-10.38,1.9,1.9,0,0,0-1.84-1.74,113.15,113.15,0,0,0-15,0A1.9,1.9,0,0,0,6.71,9.49a74.92,74.92,0,0,0-.06,10.38,2,2,0,0,0,2.1,1.81h3.81V26.5Z"/>
                        </svg>
                        <div className="lc-dots">
                            <span className="lc-dot lc-dot-1"/>
                            <span className="lc-dot lc-dot-2"/>
                            <span className="lc-dot lc-dot-3"/>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}


/* ══════════════════════════════════════════════════════
   LAUNCHER CSS
   ══════════════════════════════════════════════════════ */
const LAUNCHER_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap');

.lc-launcher {
    position: fixed;
    bottom: 1em;
    right: 0.75em;
    z-index: 2147483646;
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    animation: lc-enter 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28) both;
    animation-delay: 0.5s;
}

@keyframes lc-enter {
    0%   { opacity: 0; transform: translateY(16px) scale(0.9); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
}

.lc-input-bar {
    display: flex;
    align-items: center;
    background: #ffffff;
    border-radius: 24px;
    padding: 12px 8px 12px 20px;
    gap: 10px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04);
    transition: box-shadow 0.2s ease, transform 0.15s ease;
    min-width: 200px;
}
.lc-launcher:hover .lc-input-bar {
    box-shadow: 0 4px 12px rgba(0,0,0,0.1), 0 12px 32px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.06);
    transform: translateY(-1px);
}
.lc-input-text {
    color: #999;
    font-size: 15px;
    font-weight: 400;
    letter-spacing: 0.1px;
    flex: 1;
    white-space: nowrap;
}
.lc-input-send {
    width: 36px; height: 36px;
    border-radius: 50%;
    background: #e8e8e8;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s ease;
}
.lc-launcher:hover .lc-input-send { background: #ddd; }

.lc-bubble {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: #dddd2d;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;
    box-shadow: rgba(0,0,0,0.125) 0px 0.362176px 0.941657px -1px, rgba(0,0,0,0.18) 0px 3px 7.8px -2px;
    transition: transform 250ms cubic-bezier(0.14, 0, 0, 1);
}

.lc-launcher:active .lc-bubble {
    transform: scale(0.75);
}

.lc-inner {
    transform-origin: 50% 50%;
    transition: 200ms cubic-bezier(0.18, 0.89, 0.32, 1.28) 50ms;
}
.lc-launcher:hover .lc-inner {
    opacity: 0;
    transform: scale(0.6);
}

.lc-dots {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    opacity: 0;
    transition: opacity 150ms cubic-bezier(0.33, 0, 0.67, 1) 50ms;
    z-index: 2;
    width: 100%;
    height: 100%;
}
.lc-launcher:hover .lc-dots {
    opacity: 1;
}
.lc-dot {
    width: 3.5px;
    height: 3.5px;
    border-radius: 50%;
    background: #dddd2d;
    display: block;
}

@keyframes lc-bounce {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-3px); }
}
.lc-launcher:hover .lc-dot {
    animation: lc-bounce 0.6s ease-in-out infinite;
}
.lc-dot-1 { animation-delay: 0s !important; }
.lc-dot-2 { animation-delay: 0.15s !important; }
.lc-dot-3 { animation-delay: 0.3s !important; }

/* ══════════════════════════════════════════════════════
   FORM CARD
   ══════════════════════════════════════════════════════ */

.lc-form-overlay {
    position: fixed;
    inset: 0;
    z-index: 2147483646;
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    padding: 1em 0.75em;
    font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    animation: lc-fade-in 0.2s ease both;
}

@keyframes lc-fade-in {
    0%   { opacity: 0; }
    100% { opacity: 1; }
}

.lc-form-card {
    background: #ffffff;
    border-radius: 16px;
    padding: 32px 28px 24px;
    width: 370px;
    height: 520px;
    max-width: calc(100vw - 2em);
    max-height: calc(100vh - 2em);
    box-shadow: 0 4px 24px rgba(0,0,0,0.12), 0 12px 48px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04);
    position: relative;
    animation: lc-slide-up 0.35s cubic-bezier(0.18, 0.89, 0.32, 1.28) both;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

@keyframes lc-slide-up {
    0%   { opacity: 0; transform: translateY(24px) scale(0.96); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
}

.lc-form-close {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    background: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s ease;
}
.lc-form-close:hover {
    background: #eee;
}

.lc-form-header {
    text-align: center;
    margin-bottom: 28px;
    padding-top: 8px;
}

.lc-form-icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: #f8f8f0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;
}

.lc-form-title {
    font-size: 19px;
    font-weight: 700;
    color: #111;
    letter-spacing: -0.3px;
}

.lc-form-subtitle {
    font-size: 14px;
    color: #888;
    margin-top: 6px;
}

.lc-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
}

.lc-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.lc-label {
    font-size: 12px;
    font-weight: 700;
    color: #555;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.lc-input {
    width: 100%;
    padding: 13px 16px;
    border: 1.5px solid #e5e5e5;
    border-radius: 12px;
    font-size: 15px;
    font-family: inherit;
    color: #111;
    background: #fafafa;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    box-sizing: border-box;
}
.lc-input::placeholder {
    color: #bbb;
}
.lc-input:focus {
    border-color: #dddd2d;
    box-shadow: 0 0 0 3px rgba(221,221,45,0.15);
    background: #fff;
}

.lc-submit {
    width: 100%;
    padding: 14px 20px;
    border: none;
    border-radius: 14px;
    background: #dddd2d;
    color: #111;
    font-size: 15px;
    font-weight: 700;
    font-family: inherit;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease;
    margin-top: auto;
}
.lc-submit:hover {
    background: #e5e530;
    box-shadow: 0 2px 8px rgba(221,221,45,0.3);
}
.lc-submit:active {
    transform: scale(0.98);
}

.lc-skip {
    display: block;
    width: 100%;
    text-align: center;
    padding: 14px 0 4px;
    border: none;
    background: none;
    color: #aaa;
    font-size: 12.5px;
    font-family: inherit;
    cursor: pointer;
    transition: color 0.15s ease;
    flex-shrink: 0;
}
.lc-skip:hover {
    color: #777;
}

/* ── Mobile ── */
@media (max-width: 768px) {
    .lc-launcher {
        bottom: 12px;
        right: 12px;
        gap: 0;
    }
    .lc-input-bar { display: none; }
    .lc-bubble {
        width: 52px;
        height: 52px;
    }
    .lc-bubble svg { width: 28px; height: 28px; }
    .lc-form-overlay {
        padding: 12px;
    }
    .lc-form-card {
        width: 100%;
        height: calc(100vh - 24px);
        max-height: 520px;
    }
}
`
